import classnames from 'classnames'
import { observer } from 'mobx-react-lite'
import { type FC, useEffect, useMemo, useRef } from 'react'
import { AutomatedAvatar, Avatar, AvatarGroup, type IAvatarInfo, Icon, Typography } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import {
  DayjsFormats,
  formatPhoneNumberInternational,
  parserHtml,
  removeSpacesFromNumericString,
} from 'shared/lib'
import { type Message } from 'entities/Message/model/Message'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { textToHtml } from 'entities/Message/lib/textToHtml'
import { parseMentionsTemplate, parseNoteBody } from 'entities/Message/lib/parseNote'
import { useResultsContext } from 'pages/conversations/pages/search/ui/Results/context/resultsContext'
import styles from './styles.module.scss'

type IMessageCardProps = {
  item: Message
  onActionClick: (item: Message) => void
  isHighlighted?: boolean
}

export const MessageCard: FC<IMessageCardProps> = observer(
  ({ item, onActionClick, isHighlighted }) => {
    const entities = useEntitiesContext()
    const { resultsMessagesStore, search } = useResultsContext()
    const { contactsStore, inboxesStore, usersStore } = entities

    const contact = contactsStore.getItem(item.contact_id)
    const conversation = resultsMessagesStore.getConversationItem(item.conversation_id)
    const user = usersStore.getItem(item.user_id)
    const isSent = item.isOutbound
    const avatarInfo = user?.avatarInfo
    const isShowAvatar = isSent && !!avatarInfo
    const contactsInfo =
      conversation?.contactsIds.reduce<IAvatarInfo[]>((state, id) => {
        const contact = contactsStore.getItem(id)

        if (contact) state.push(contact.avatarInfo)

        return state
      }, []) || []
    const inbox = inboxesStore.getItem(conversation?.inbox_id)
    const isAutomated = item.isAutomated
    const automatedInfo = item.automatedInfo

    if (!contactsInfo.length) return null
    if (!inbox) return null

    let bodyParse = textToHtml(entities, {
      text: item.body,
      replaceNewRow: true,
      noReplaceMergeField: true,
      noEscapeHtml: item.isAirCall,
    })

    if (!item.isNote) bodyParse = parseMentionsTemplate(bodyParse, usersStore.usersMap)
    bodyParse = parseNoteBody(bodyParse, usersStore.usersMap)

    const renderInfo = () => {
      if (conversation?.isGroup) {
        return (
          <>
            <div className={styles.avatar}>
              <AvatarGroup items={contactsInfo} size={32} />
            </div>
            <div className={styles.info__content}>
              {conversation.title && (
                <Typography
                  className={styles.info__name}
                  ariaLabel={'info__name'}
                  variant={'body-md-medium'}
                  ellipsis
                >
                  {conversation.title}
                </Typography>
              )}
            </div>
          </>
        )
      }

      if (contact) {
        const number = formatPhoneNumberInternational(contact.national_number)

        return (
          <>
            <div className={styles.avatar}>
              <Avatar info={contact.avatarInfo} size={32} />
            </div>
            <div className={styles.info__content}>
              {contact.full_name && (
                <Typography
                  className={styles.info__name}
                  ariaLabel={'info__name'}
                  variant={'body-md-medium'}
                  ellipsis
                >
                  {contact.full_name}
                </Typography>
              )}

              {number && (
                <Typography
                  className={styles.info__phone}
                  ariaLabel={'info__phone'}
                  variant={contact.full_name ? 'body-md-regular' : 'body-md-medium'}
                  ellipsis
                >
                  {number}
                </Typography>
              )}
            </div>
          </>
        )
      }

      return null
    }

    const avatarMessageContent = useMemo(() => {
      if (isAutomated) {
        return (
          <div className={styles.user__avatar}>
            <AutomatedAvatar size={'small'} automatedInfo={automatedInfo} />
          </div>
        )
      }
      return (
        isShowAvatar && (
          <div className={styles.user__avatar}>
            <Avatar size={16} info={user.avatarInfo} />

            {user?.full_name && (
              <Typography
                variant={'body-md-regular'}
                ariaLabel={'name'}
                className={styles.user__name}
              >
                {user.full_name}
              </Typography>
            )}
          </div>
        )
      )
    }, [isShowAvatar, isAutomated, user, automatedInfo])

    const ref = useRef<HTMLDivElement>(null)

    useEffect(() => {
      if (isHighlighted && ref.current) {
        ref.current.scrollIntoView({ block: 'nearest' })
      }
    }, [isHighlighted])

    return (
      <div
        ref={ref}
        className={classnames(styles.wrap, {
          [styles.highlighted]: isHighlighted,
        })}
        onClick={() => {
          onActionClick(item)
        }}
      >
        <div className={styles.sectionTop}>
          <div className={styles.info}>{renderInfo()}</div>
          <div className={styles.inbox}>
            <Icon className={styles.inbox__icon} icon='inboxes2' fontSize={16} />
            <Typography
              className={styles.inbox__text}
              variant={'body-md-regular'}
              ariaLabel={'inbox_label'}
              ellipsis
            >
              {inbox.name}
            </Typography>
          </div>
        </div>
        <div className={styles.sectionBottom}>
          <div className={styles.user}>
            {avatarMessageContent}
            <Typography
              tag={'div'}
              variant={'body-md-regular'}
              className={styles.user__date}
              ariaLabel={'date'}
              ellipsis
            >
              {uiStore.dayjs(item.created_at).format(DayjsFormats.full6)}
            </Typography>
          </div>

          <Typography
            tag={'div'}
            variant={'body-md-regular'}
            className={styles.message}
            ariaLabel={'date'}
            ellipsis
          >
            {parserHtml(bodyParse, {
              search: removeSpacesFromNumericString(search),
            })}
          </Typography>
        </div>
      </div>
    )
  }
)
